import {
	createColumnHelper,
	ColumnDef,
} from "@tanstack/react-table";

// Custom components

import { Group } from 'api/types';

import { Header } from 'views/admin/simulator/components/datatable';
import { ApiDeleteGroup, ApiEnableGroup, ApiUpdateGroup, ApiCreateGroup } from 'api/auth/management/group';
import { useState } from 'react';

import { DynamicFormModal, FormField, valueOption, DynamicModalFormOptions } from 'components/managementModals/DynamicFormModal';

import ManagementTable from './BaseTable';
import { TextValue, CheckValue, ManagementModalProps, EditModal } from './BaseTable';

const helper = createColumnHelper<Group>();

const groupTypeOptions: valueOption[] = [
	{
		label: 'Usuarios',
		value: '0'
	},
	{
		label: 'Tableros',
		value: '1'
	},
	{
		label: 'Tableros de Usuario',
		value: '2'
	},
	{
		label: 'Generico',
		value: '3'
	},
]

export const GroupManagementModal = (props: ManagementModalProps) => {

	const { object, options, mode, viewReloader } = props

	const create = mode === 'create'
	const title = create ? 'Crear Grupo' : 'Actualizar Grupo'
	const group: Group = object

	const [error, setError] = useState(null)
	const { isOpen, onClose } = props.disclosure

	let permissionOptions = options.filter((o) => o.for === "permissions")[0]?.options
	permissionOptions = permissionOptions ?? []

	let requestBody: { [x: string]: any } = {}
	let modified = false

	const genericHandler = (ev: any) => {
		let value: string = ev.target.value
		let attribute: string = ev.target.name
		if (value.length === 0) { return }
		requestBody[attribute] = value
		modified = true
	}

	const checkHandler = (ev: any) => {
		let value: string = ev.target.checked
		let attribute: string = ev.target.name
		if (value.length === 0) { return }
		requestBody[attribute] = value
		modified = true
	}

	const optionHandler = (attribute: string) => {
		return function (ev: any) {
			let value: string = ev.value
			requestBody[attribute] = value
			modified = true
		}
	}

	const permissionHandler = (ev: any[]) => {
		requestBody['permissions'] = ev.map((e) => ({ codename: e.value }))
		modified = true
	}

	let matchedType = group ? groupTypeOptions.filter((g) => g.value === group.type.toString())[0] : null
	const formFields: FormField[] = [
		{
			displayableName: 'Nombre',
			valueName: 'name',
			type: 'text',
			required: create,
			defaultValue: group?.name,
			events: [{ on: 'Change', action: genericHandler }]
		},
		{
			displayableName: 'Descripción',
			valueName: 'description',
			type: 'text',
			required: create,
			defaultValue: group?.description,
			events: [{ on: 'Change', action: genericHandler }]
		},
		{
			displayableName: 'Activo',
			valueName: 'is_active',
			type: 'boolean',
			required: create,
			defaultValue: group?.is_active,
			events: [{ on: 'Change', action: checkHandler }]
		},
		{
			displayableName: 'Tipo',
			valueName: 'type',
			type: 'option',
			options: groupTypeOptions,
			required: create,
			defaultValue: matchedType,
			events: [{ on: 'Change', action: optionHandler('type') }]
		},
		{
			displayableName: 'Permisos',
			valueName: 'permissions',
			type: 'multi-option',
			options: permissionOptions,
			required: create,
			defaultValue: group?.permissions.map((p) => ({ label: p.name, value: p.codename })),
			events: [{ on: 'Change', action: permissionHandler }]
		},
	]

	const customOnClose = () => {
		setError(null)
		onClose()
		if (modified) { viewReloader(Math.random()) }
	}

	const submitAction = async () => {

		if (modified) {
			let method = null
			if (create) {
				method = ApiCreateGroup(requestBody)
			} else {
				method = ApiUpdateGroup(group.id, requestBody)
			}
			method.then((_) => {
				customOnClose()
			}).catch((err) => {
				let data: { [x: string]: string } = err.response.data
				let fields: { field: string, description: string }[] = []
				for (const key in data) {
					fields.push({
						field: key,
						description: typeof data[key] !== 'string' ? data[key][0] : data[key]
					})
				}
				fields = fields.map((f) => {
					let _f = formFields.filter((ff) => ff.valueName === f.field)
					return _f[0] && typeof f.description === 'string' ? { field: _f[0].displayableName, description: f.description } : null
				}).filter((r) => r !== null)
				fields.length > 0 ? setError({
					message: "Revise los siguientes campos:",
					fieldErrors: fields
				}) : setError({ message: "Ocurrió un error inesperado." })

			})
		} else { customOnClose() }

	}

	return (
		<>
			<DynamicFormModal
				fields={formFields}
				isOpen={isOpen}
				onClose={customOnClose}
				title={title}
				buttonText={"Enviar"}
				buttonOnClick={submitAction}
				error={error}
			/>
		</>

	)
}

export default function GroupTable(props: { data: Group[], options: DynamicModalFormOptions[], reloader: (key: number) => void }) {

	const { data, options, reloader } = props;

	const GroupLabel = (props: { val: any }) => {
		const { val } = props
		let t = groupTypeOptions.filter((o) => o.value == val)[0].label
		return <TextValue val={t} />
	}

	const columns: ColumnDef<Group, any>[] = [
		helper.accessor('name', {
			id: 'name',
			header: () => <Header text="Grupo" />,
			cell: (info: any) => <TextValue val={info.getValue()} />
		}),
		helper.accessor('description', {
			id: 'description',
			header: () => <Header text="Descripcion" />,
			cell: (info: any) => <TextValue val={info.getValue()} />
		}),
		helper.accessor('type', {
			id: 'type',
			header: () => <Header text="Tipo" />,
			cell: (info: any) => <GroupLabel val={info.getValue()} />
		}),
		helper.accessor((row) => { return { id: row.id, is_active: row.is_active } }, {
			id: 'is_active',
			header: () => <Header text="Activo" />,
			cell: (info: any) => <CheckValue val={info.getValue()} methodCheck={ApiEnableGroup} methodUncheck={ApiDeleteGroup} reloader={reloader} />
		}),
		helper.accessor((row) => row, {
			id: 'edit',
			header: () => <Header text="Editar" />,
			cell: (info: any) => <EditModal
				modalComponent={GroupManagementModal}
				modalProps={{
					mode: 'update',
					object: info.getValue(),
					options: options,
					viewReloader: reloader
				}}
				disabled={false} />
		})
	];

	return (<ManagementTable data={data} columns={columns} />)
}